import { useState } from 'react'
import JsBarcode from 'jsbarcode'
import { core } from '@musinsa/pbo-core'
import { notice } from '@/components/pbo/notice'
import { GeneralInboundRow } from '../queries/useGetInboundList'
import { BARCODE_COPIES_MAX, BARCODE_SPECS, BarcodeSpec, DEFAULT_BARCODE_SPEC } from './consts'
import { BarcodeSheetPreview } from './BarcodeSheetPreview'

/** 컨덕터 `core.FormItem` 라벨 고정폭 — 산출물이 `labelWidth={88}` 로 내보낸다(DS 요소 기본값은 160). */
const FI_LABEL_WIDTH = 88

// 바코드 출력 모달 — 컨덕터 `barcodePrintModal`(`v2Skeleton.ts:520`) 트리 그대로:
// `core.Modal(바코드 출력·720·프린트/취소) > Flex(gap 24)[Flex(vertical 20)[FormItem 라벨지 규격 > Select · FormItem 매수 > InputNumber] · BarcodeSheetPreview] + Flex(vertical 4)[※ 안내 2줄]`.
// 1.0 PRINT(printCode='BARCODE2_ASN') 파리티 — 선택 행의 바코드를 CODE128 라벨로 렌더해 브라우저 인쇄로 넘긴다. 서버 인쇄 API 는 1.0 에도 없다.

/** 규격별 인쇄 격자 — 미리보기(SHEET_LAYOUT)와 같은 칸 수를 쓴다 */
const PRINT_LAYOUT: Record<BarcodeSpec, { columns: number; labelHeightMm: number; pageSize: string; marginMm: number }> = {
  'A4(3X4)': { columns: 3, labelHeightMm: 69, pageSize: 'A4', marginMm: 8 },
  'A4(3X8)': { columns: 3, labelHeightMm: 34, pageSize: 'A4', marginMm: 8 },
  'A4(바코드)': { columns: 1, labelHeightMm: 55, pageSize: 'A4', marginMm: 8 },
  '라벨(1X2)': { columns: 1, labelHeightMm: 70, pageSize: '100mm 150mm', marginMm: 4 },
}

// BARCODE/GDS_NM/ORD_OPT_NO 는 셀 편집·엑셀 업로드로 임의 입력이 가능하다 — document.write 전 반드시 이스케이프한다.
const escapeHtml = (value: string): string =>
  value.replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c] ?? c)

const barcodeSvg = (value: string): string => {
  const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg')
  try {
    JsBarcode(svg, value, { format: 'CODE128', displayValue: true, fontSize: 12, height: 40, margin: 4 })
  } catch {
    return `<div class="invalid">잘못된 바코드: ${escapeHtml(value)}</div>`
  }
  svg.setAttribute('preserveAspectRatio', 'xMidYMid meet')
  svg.style.maxWidth = '100%'
  svg.style.maxHeight = '70%'
  return svg.outerHTML
}

const openPrintWindow = (rows: GeneralInboundRow[], spec: BarcodeSpec, copies: number) => {
  const layout = PRINT_LAYOUT[spec]
  const printable = rows.filter((r) => r.BARCODE)
  const labels = printable
    .flatMap((row) => Array.from({ length: copies }, () => row))
    .map(
      (row) => `
      <div class="label">
        ${barcodeSvg(row.BARCODE)}
        <div class="name">${escapeHtml(row.GDS_NM ?? '')}</div>
        <div class="code">${escapeHtml(row.ORD_OPT_NO ?? '')}</div>
      </div>`,
    )
    .join('')
  const win = window.open('', '_blank', 'width=900,height=1200')
  if (!win) {
    notice.failure('팝업이 차단되어 인쇄 창을 열 수 없습니다.')
    return
  }
  win.document.write(`<!doctype html><html><head><title>바코드 출력 (${escapeHtml(spec)})</title>
    <style>
      @page { size: ${layout.pageSize}; margin: ${layout.marginMm}mm; }
      body { margin: 0; font-family: 'Pretendard', -apple-system, system-ui, sans-serif; }
      .sheet { display: grid; grid-template-columns: repeat(${layout.columns}, 1fr); gap: 2mm; }
      .label { height: ${layout.labelHeightMm}mm; border: 0.2mm dashed #ccc; display: flex; flex-direction: column;
               align-items: center; justify-content: center; overflow: hidden; padding: 1mm; box-sizing: border-box;
               break-inside: avoid; text-align: center; }
      .name { font-size: 10px; width: 100%; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
      .code { font-size: 9px; color: #555; }
      .invalid { color: red; font-size: 10px; }
    </style></head><body><div class="sheet">${labels}</div></body></html>`)
  win.document.close()
  win.focus()
  // 인쇄 다이얼로그 — write/close **뒤**에 건다. document.open()(write 가 암묵 호출)은 window 의 이벤트 핸들러를 지우므로
  // 앞에 걸어 둔 onload 는 사라진다(리뷰 지적 2026-09-11 — 인쇄 창이 안 뜨던 원인). 인라인 문서는 close() 시점에 이미 complete 일 수
  // 있어 그 경우 바로 부르고, 아니면 load 를 한 번만 기다린다. 인쇄/취소 후 창은 사용자가 닫는다(1.0 동일 UX).
  const print = () => win.print()
  if (win.document.readyState === 'complete') print()
  else win.addEventListener('load', print, { once: true })
}

interface Props {
  open: boolean
  rows: GeneralInboundRow[]
  onClose: () => void
}

export const BarcodePrintModal = ({ open, rows, onClose }: Readonly<Props>) => {
  const [spec, setSpec] = useState<BarcodeSpec>(DEFAULT_BARCODE_SPEC)
  const [copies, setCopies] = useState(1)

  const printable = rows.filter((r) => r.BARCODE).length

  return (
    <core.Modal
      open={open}
      title="바코드 출력"
      width={720}
      okText="프린트"
      cancelText="취소"
      onOk={() => {
        // 컨덕터는 확인 상시 활성 — 출력할 바코드가 없으면 검증 토스트로 알린다.
        if (printable === 0) return notice.invalid('바코드가 있는 행이 없습니다.')
        openPrintWindow(rows, spec, copies)
        return onClose()
      }}
      onCancel={onClose}
    >
      <core.Flex gap={24} align="flex-start">
        <core.Flex vertical gap={20} align="stretch" style={{ flex: 1 }}>
          {/* 컨덕터 `bpSpecItem`/`bpCopiesItem` = `core.FormItem`(라벨 왼쪽 88px) — pbo-core 0.7.9 게시 요소를 그대로 쓴다. */}
          <core.FormItem label="라벨지 규격" labelWidth={FI_LABEL_WIDTH}>
            <core.Select
              value={spec}
              options={BARCODE_SPECS.map((s) => ({ label: s, value: s }))}
              onChange={(value: BarcodeSpec) => setSpec(value)}
              style={{ width: '100%' }}
            />
          </core.FormItem>
          <core.FormItem label="매수" labelWidth={FI_LABEL_WIDTH}>
            {/* 컨덕터 노드는 `addonAfter: '매'` 지만 antd 5.29 가 InputNumber 의 addonAfter 를 deprecate 하고 Space.Compact 를 가리킨다.
                단위 한 글자를 붙이자고 가짜 입력칸을 세우지 않는다 — `suffix` 가 지원 프롭이고 컨트롤이 하나로 유지된다.
                형제 화면(gr02V2·rgr11V2 손코드)도 같은 판단으로 옮겼다(a364c19d). */}
            <core.InputNumber min={1} max={BARCODE_COPIES_MAX} value={copies} onChange={(v) => setCopies(Number(v) || 1)} suffix="매" style={{ width: 120 }} />
          </core.FormItem>
        </core.Flex>
        <BarcodeSheetPreview spec={spec} />
      </core.Flex>
      {/* 산출물 `bpNotice` — 위 본문 Flex 와의 간격 지정 없음(산출물 그대로). */}
      <core.Flex vertical gap={4} align="stretch">
        <core.Typography.Text type="secondary" style={{ fontSize: 13 }}>
          ※ 본 미리보기는 선택하신 규격의 샘플 이미지이며, 실제 출력 데이터와 무관합니다.
        </core.Typography.Text>
        <core.Typography.Text type="secondary" style={{ fontSize: 13 }}>
          ※ 라벨프린터로 출력을 권장합니다.
        </core.Typography.Text>
      </core.Flex>
    </core.Modal>
  )
}

export default BarcodePrintModal
